import { useCallback, useEffect, useState } from 'react';
import { getErrorMessage } from '../../lib/errors';
import type { NewsFeedRead } from '../../types/api';
import { fetchNews } from './newsApi';

export interface NewsResult {
  feed: NewsFeedRead | null;
  /** First load for this business. A refresh is not a loading state. */
  isLoading: boolean;
  /** A user-asked re-pull is in flight; the current items stay on screen. */
  isRefreshing: boolean;
  error: string | null;
  /** Go back out to the sources (`refresh=true`). Spends no generation. */
  refresh: () => void;
}

/** Result of the last completed load, tagged with the business it belongs to. */
interface LoadedNews {
  businessId: string;
  feed: NewsFeedRead | null;
  error: string | null;
}

/** What the current effect run is asking for. The nonce is what re-runs it. */
interface FeedRequest {
  nonce: number;
  refresh: boolean;
}

const FIRST_REQUEST: FeedRequest = { nonce: 0, refresh: false };

/**
 * The industry newsfeed for one business.
 *
 * Same shape as the knowledge-base loaders: one effect, one AbortController, no
 * synchronous state write, and the visible state derived during render so items
 * from a previous business can never leak onto the screen.
 *
 * A failed refresh keeps the headlines that are already up and reports the
 * failure beside them — a stale list is more use than an empty one.
 */
export function useNews(businessId: string | null): NewsResult {
  const [loaded, setLoaded] = useState<LoadedNews | null>(null);
  const [request, setRequest] = useState<FeedRequest>(FIRST_REQUEST);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    if (!businessId) return;

    const controller = new AbortController();
    let cancelled = false;

    void (async () => {
      try {
        const feed = await fetchNews(businessId, request.refresh, controller.signal);
        if (cancelled) return;
        setLoaded({ businessId, feed, error: null });
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return;
        const error = getErrorMessage(caught, 'Could not load the news.');
        setLoaded((current) => ({
          businessId,
          feed: current?.businessId === businessId ? current.feed : null,
          error,
        }));
      } finally {
        if (!cancelled) setIsRefreshing(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [businessId, request]);

  const refresh = useCallback(() => {
    setIsRefreshing(true);
    setRequest((current) => ({ nonce: current.nonce + 1, refresh: true }));
  }, []);

  const isCurrent = loaded !== null && loaded.businessId === businessId;

  return {
    feed: isCurrent ? loaded.feed : null,
    isLoading: businessId !== null && !isCurrent,
    isRefreshing,
    error: isCurrent ? loaded.error : null,
    refresh,
  };
}
